import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// BPO RH — Adiantamento salarial: a operadora importa o PDF "Extrato Mensal — Cálculo:
// Adiantamento" do Domínio (um por filial), o Saber monta a relação por colaborador,
// ela marca quem foi pago (como no VA/VT) e gera a planilha de Contas a Pagar da Omie
// (mesmas regras da Importação Omie; vencimento dia 20). Backend: core/views_adiantamento.py
const API = process.env.REACT_APP_API_BASE;
const URL_ADTO = `${API}/api/bporh/adiantamento/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const erroMsg = (e, padrao) => e.response?.data?.mensagem || e.response?.data?.erro || padrao;
const cnpjFmt = (c) => {
  const d = String(c || '').replace(/\D/g, '');
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : (c || '—');
};
// o adiantamento é pago no próprio mês (dia 20): a competência padrão é o mês atual
const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function BpoRhAdiantamento() {
  const [params, setParams] = useSearchParams();
  const clienteId = params.get('cliente') || '';
  const mes = params.get('mes') || mesAtual();
  const [clientes, setClientes] = useState(null);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState('');
  const [fornecedor, setFornecedor] = useState('nome');
  // envio da planilha por e-mail: {para, copia, operador_id, previa, carregando}
  const [email, setEmail] = useState(null);
  const arquivoRef = useRef(null);
  const reqRef = useRef(0);

  const trocar = (novo) => {
    const p = new URLSearchParams(params);
    Object.entries(novo).forEach(([k, val]) => (val ? p.set(k, val) : p.delete(k)));
    setParams(p);
  };

  const carregarClientes = useCallback(() => {
    axios.get(URL_ADTO, { params: { mes }, headers: headers() })
      .then((r) => setClientes(r.data.clientes || []))
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível carregar as empresas')}`));
  }, [mes]);

  const carregar = useCallback(() => {
    if (!clienteId) { setDados(null); return; }
    const id = ++reqRef.current;
    setCarregando(true);
    axios.get(URL_ADTO, { params: { cliente_id: clienteId, mes, _: Date.now() }, headers: headers() })
      .then((r) => { if (id === reqRef.current) setDados(r.data); })
      .catch((e) => { if (id === reqRef.current) { setDados(null); notificar(`❌ ${erroMsg(e, 'Não foi possível carregar')}`); } })
      .finally(() => { if (id === reqRef.current) setCarregando(false); });
  }, [clienteId, mes]);

  useEffect(() => { carregarClientes(); }, [carregarClientes]);
  useEffect(() => { carregar(); }, [carregar]);

  const importar = (ev) => {
    const f = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!f) return;
    const fd = new FormData();
    fd.append('arquivo', f);
    fd.append('cliente_id', clienteId);
    setOcupado('importar');
    axios.post(URL_ADTO, fd, { headers: headers(), timeout: 240000 })
      .then((r) => {
        notificar(`✅ ${r.data.mensagem}`);
        // a competência e a empresa vêm do PDF (CNPJ): a tela vai para elas
        const outraEmpresa = String(r.data.cliente_id) !== String(clienteId);
        if (outraEmpresa || (r.data.mes && r.data.mes !== mes)) {
          trocar({ cliente: String(r.data.cliente_id), mes: r.data.mes });
        } else setDados(r.data);
        carregarClientes();
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível ler o extrato')}`))
      .finally(() => setOcupado(''));
  };

  const acao = (corpo, chave) => {
    setOcupado(chave);
    return axios.post(URL_ADTO, { cliente_id: Number(clienteId), mes, ...corpo }, { headers: headers() })
      .then((r) => { setDados((d) => ({ ...d, ...r.data })); if (r.data.mensagem) notificar(`✅ ${r.data.mensagem}`); carregarClientes(); })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível salvar')}`))
      .finally(() => setOcupado(''));
  };

  const marcarPago = (cpfs, pago) => cpfs.length && acao({ acao: 'pagar', cpfs, pago }, 'pagar');
  const salvarDepto = (p, departamento) => acao({ acao: 'departamento', cpf: p.cpf, nome: p.nome, departamento }, `dep-${p.cpf}`);
  const excluirLote = (cnpj) => acao({ acao: 'excluir_lote', cnpj }, `lote-${cnpj}`);

  const exportar = () => {
    setOcupado('exportar');
    axios.get(URL_ADTO, { params: { exportar: 1, cliente_id: clienteId, mes, fornecedor, _: Date.now() }, headers: headers(), responseType: 'blob' })
      .then((r) => {
        const cd = r.headers['content-disposition'] || '';
        const m = cd.match(/filename="?([^";]+)"?/i);
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = m ? m[1] : `Omie_Contas_Pagar_Adto_${mes}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
      })
      .catch(async (e) => {
        let msg = 'Não foi possível gerar a planilha';
        try { msg = JSON.parse(await e.response.data.text()).mensagem || msg; } catch (_) { /* mantém */ }
        notificar(`❌ ${msg}`);
      })
      .finally(() => setOcupado(''));
  };

  const abrirEmail = () => {
    const e = dados?.envio || {};
    const novo = { para: e.para || '', copia: e.copia || '', operador_id: String(e.operador_sugerido_id || ''),
                   previa: null, carregando: false };
    setEmail(novo);
    verPrevia(novo);
  };

  const verPrevia = (e) => {
    setEmail((x) => ({ ...(x || e), carregando: true }));
    axios.post(URL_ADTO, {
      acao: 'email', enviar: false, cliente_id: Number(clienteId), mes, fornecedor,
      destinatarios: e.para, copia: e.copia, operador_id: e.operador_id || undefined,
    }, { headers: headers() })
      .then((r) => setEmail((x) => ({ ...(x || e), previa: r.data.previa, carregando: false })))
      .catch((er) => { setEmail((x) => (x ? { ...x, carregando: false } : x)); notificar(`❌ ${erroMsg(er, 'Não foi possível montar o e-mail')}`); });
  };

  const enviarEmail = () => {
    setOcupado('email');
    axios.post(URL_ADTO, {
      acao: 'email', enviar: true, cliente_id: Number(clienteId), mes, fornecedor,
      destinatarios: email.para, copia: email.copia, operador_id: email.operador_id || undefined,
      assunto: email.previa?.assunto, corpo_html: email.previa?.corpo_html,
    }, { headers: headers(), timeout: 120000 })
      .then((r) => { notificar(`✅ ${r.data.mensagem}`); setDados((d) => ({ ...d, ...r.data })); setEmail(null); })
      .catch((er) => notificar(`❌ ${erroMsg(er, 'Não foi possível enviar')}`))
      .finally(() => setOcupado(''));
  };
  const salvarEmails = () => {
    setOcupado('salvar-email');
    axios.post(URL_ADTO, { acao: 'salvar_emails', cliente_id: Number(clienteId), mes, destinatarios: email.para, copia: email.copia },
      { headers: headers() })
      .then((r) => { notificar(`✅ ${r.data.mensagem}`); setDados((d) => ({ ...d, envio: r.data.envio })); })
      .catch((er) => notificar(`❌ ${erroMsg(er, 'Não foi possível salvar os e-mails')}`))
      .finally(() => setOcupado(''));
  };
  // mudou destinatário/remetente: a prévia velha não vale mais
  const mudarEmail = (campo, valor) => setEmail((x) => ({ ...x, [campo]: valor, previa: null }));

  const pessoas = dados?.pessoas || [];
  const resumo = dados?.resumo || {};
  const aPagar = pessoas.filter((p) => !p.pago && (p.liquido || 0) > 0).map((p) => p.cpf);
  const pagos = pessoas.filter((p) => p.pago).map((p) => p.cpf);
  const departamentos = dados?.departamentos || [];

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-hand-holding-dollar header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">BPO RH — Adiantamento salarial</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 230 }} value={clienteId} onChange={(e) => trocar({ cliente: e.target.value })} aria-label="Empresa">
            <option value="">{clientes ? 'Escolha a empresa…' : 'Carregando…'}</option>
            {(clientes || []).map((c) => (
              <option key={c.id} value={c.id}>{c.cliente}{c.filiais ? ` · ${c.pagos ? `${c.pagos} pago(s)` : 'importado'}` : ''}</option>
            ))}
          </select>
          <input type="month" className="form-control form-control-sm" style={{ width: 150 }} value={mes} onChange={(e) => e.target.value && trocar({ mes: e.target.value })} aria-label="Competência" />
          <button type="button" className="gr-btn" onClick={carregar} disabled={carregando || !clienteId}><i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar</button>
        </div>
      </div>

      {!clienteId && <div className="text-muted small py-4 text-center">Escolha a empresa para começar.</div>}

      {clienteId && dados && (<>
        {/* importar + extratos do mês (um por filial/CNPJ) */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <input type="file" accept="application/pdf,.pdf" ref={arquivoRef} onChange={importar} style={{ display: 'none' }} />
          <button type="button" className="btn btn-sm btn-primary" onClick={() => arquivoRef.current?.click()} disabled={!!ocupado}>
            {ocupado === 'importar'
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Lendo o extrato…</>
              : <><i className="fas fa-file-pdf me-2"></i>Importar extrato (PDF)</>}
          </button>
          <span className="small text-muted">
            Extrato Mensal do Domínio, cálculo Adiantamento — um PDF por filial. Reimportar o mesmo mês e filial substitui.
          </span>
        </div>

        {(dados.lotes || []).map((lt) => {
          const diverge = lt.total_impresso != null && Math.abs(lt.total_impresso - lt.total_lido) > 0.05;
          return (
            <div key={lt.cnpj} className="border rounded px-3 py-2 mb-2 small d-flex flex-wrap align-items-center gap-3" style={{ background: '#f8f9fa' }}>
              <span><i className="fas fa-building me-1 text-muted"></i><b>{lt.projeto || lt.empresa}</b> · CNPJ {cnpjFmt(lt.cnpj)}</span>
              <span>{lt.qtd} colaborador(es)</span>
              <span>Lido: <b>{brl(lt.total_lido)}</b></span>
              <span className={diverge ? 'text-danger fw-bold' : 'text-success'}>
                {lt.total_impresso == null ? 'sem Líquido Geral no PDF' : `Líquido Geral do PDF: ${brl(lt.total_impresso)}${diverge ? ' — diverge!' : ' ✓'}`}
              </span>
              {lt.origem === 'ia' && <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle">lido pela IA — confira</span>}
              <span className="text-muted ms-auto">{lt.importado_em}{lt.importado_por ? ` · ${lt.importado_por}` : ''}</span>
              <button type="button" className="btn btn-sm btn-light border p-1" title="Remover este extrato do mês" disabled={!!ocupado}
                onClick={() => excluirLote(lt.cnpj)}>
                <i className="fas fa-trash text-danger" style={{ fontSize: '.75rem' }}></i>
              </button>
              {(lt.avisos || []).length > 0 && (
                <div className="w-100 text-warning-emphasis">{lt.avisos.map((a, i) => <div key={i}><i className="fas fa-triangle-exclamation me-1"></i>{a}</div>)}</div>
              )}
            </div>
          );
        })}

        {!pessoas.length && (
          <div className="text-muted small py-4 text-center">Nenhum extrato importado em {dados.mes_rotulo}. Importe o PDF do adiantamento.</div>
        )}

        {pessoas.length > 0 && (<>
          {/* resumo + ações */}
          <div className="d-flex flex-wrap align-items-center gap-3 my-3">
            <span><b>{resumo.qtd}</b> colaborador(es) · total <b>{brl(resumo.total)}</b></span>
            <span className="text-success"><i className="fas fa-check-circle me-1"></i>{resumo.pagos} pago(s) · {brl(resumo.total_pago)}</span>
            <span className="text-muted small">Vencimento na Omie: <b>{dados.vencimento}</b></span>
            <div className="ms-auto d-flex flex-wrap gap-2 align-items-center">
              <button type="button" className="btn btn-sm btn-outline-success" disabled={!!ocupado || !aPagar.length} onClick={() => marcarPago(aPagar, true)}>
                <i className="fas fa-check-double me-1"></i>Marcar todos como pagos{aPagar.length ? ` (${aPagar.length})` : ''}
              </button>
              {pagos.length > 0 && (
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!!ocupado} onClick={() => marcarPago(pagos, false)}>
                  <i className="fas fa-rotate-left me-1"></i>Desmarcar todos
                </button>
              )}
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={fornecedor} onChange={(e) => setFornecedor(e.target.value)}
                title="Coluna Fornecedor da Omie" aria-label="Fornecedor na Omie">
                <option value="nome">Fornecedor: nome</option>
                <option value="cpf">Fornecedor: CPF</option>
              </select>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={exportar} disabled={!!ocupado || resumo.pendentes_omie > 0}
                title="Baixar a planilha Omie (só para conferir — o envio é pelo e-mail)">
                <i className={`fas ${ocupado === 'exportar' ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={abrirEmail} disabled={!!ocupado || resumo.pendentes_omie > 0}
                title={resumo.pendentes_omie ? 'Escolha o departamento de quem está pendente' : 'Mandar a planilha Omie por e-mail, já anexada'}>
                <i className="fas fa-paper-plane me-1"></i>Enviar planilha por e-mail
              </button>
            </div>
          </div>
          {resumo.pendentes_omie > 0 && (
            <div className="alert alert-warning small py-2">
              {resumo.pendentes_omie} colaborador(es) sem departamento na Omie — escolha na coluna Departamento para liberar a planilha.
            </div>
          )}
          {(dados.avisos_omie || []).length > 0 && (
            <div className="alert alert-light border small py-2">{dados.avisos_omie.map((a, i) => <div key={i}>{a}</div>)}</div>
          )}

          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle" style={{ fontSize: '.85rem' }}>
              <thead className="table-light">
                <tr>
                  <th>Colaborador</th><th>CPF</th><th>Cargo</th>
                  <th className="text-end">Salário</th><th className="text-end">Proventos</th>
                  <th className="text-end">Descontos</th><th className="text-end">Líquido</th>
                  <th>Departamento (Omie)</th><th className="text-center">Pago</th>
                </tr>
              </thead>
              <tbody>
                {pessoas.map((p) => (
                  <tr key={p.cpf + p.cnpj} style={p.pago ? { background: '#f1faf5' } : undefined}>
                    <td>
                      <div className="fw-semibold">{p.nome}</div>
                      {p.alerta && <div className="small text-warning-emphasis"><i className="fas fa-triangle-exclamation me-1"></i>{p.alerta}</div>}
                    </td>
                    <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{p.cpf_mascarado || '—'}</td>
                    <td className="small">{p.cargo || '—'}</td>
                    <td className="text-end text-muted">{brl(p.salario)}</td>
                    <td className="text-end">{brl(p.proventos)}</td>
                    <td className="text-end" title={(p.descontos_itens || []).map((d) => `${d.descricao}: ${brl(d.valor)}`).join('\n') || undefined}>
                      {p.descontos ? <span className="text-danger">{brl(p.descontos)}</span> : <span className="text-muted">—</span>}
                      {(p.descontos_itens || []).length > 0 && (
                        <div className="small text-muted">{p.descontos_itens.map((d) => d.descricao.toLowerCase()).join(' · ')}</div>
                      )}
                    </td>
                    <td className="text-end fw-bold">{brl(p.liquido)}</td>
                    <td style={{ minWidth: 170 }}>
                      {p.pendencias?.includes('departamento') ? (
                        <select className="form-select form-select-sm border-warning" value="" disabled={!!ocupado || !departamentos.length}
                          onChange={(e) => e.target.value && salvarDepto(p, e.target.value)}>
                          <option value="">{departamentos.length ? 'Escolha…' : 'sem departamentos na config'}</option>
                          {departamentos.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      ) : (
                        <span className="small">{p.departamento || '—'}<span className="text-muted d-block">{p.projeto || 'sem projeto'}</span></span>
                      )}
                    </td>
                    <td className="text-center" title={p.pago ? `Pago por ${p.pago_por || '—'} em ${p.pago_em || '—'}` : 'Marcar como pago'}>
                      <input type="checkbox" className="form-check-input" checked={p.pago} disabled={!!ocupado || !(p.liquido > 0)}
                        onChange={(e) => marcarPago([p.cpf], e.target.checked)} aria-label={`Pago: ${p.nome}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={6} className="text-end">Total</td>
                  <td className="text-end">{brl(resumo.total)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="small text-muted">Observação na Omie: “{dados.observacao}”.</div>
          {(dados.envio?.historico || []).length > 0 && (
            <div className="small text-muted mt-1">
              <i className="fas fa-envelope-circle-check me-1 text-success"></i>
              Planilha enviada: {dados.envio.historico.map((h) => `${h.em} para ${h.para}${h.por ? ` (${h.por})` : ''}`).join(' · ')}
            </div>
          )}
        </>)}
      </>)}
      {email && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-paper-plane me-2"></i>Enviar planilha do adiantamento
                  </h5>
                  <small style={{ color: '#6c757d' }}>{dados?.cliente} · {dados?.mes_rotulo} · {resumo.qtd} colaborador(es) · {brl(resumo.total)}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setEmail(null)} disabled={ocupado === 'email'}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Para</label>
                    <input type="text" className="form-control form-control-sm" placeholder="financeiro@cliente.com.br"
                      value={email.para} onChange={(e) => mudarEmail('para', e.target.value)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Cópia (opcional)</label>
                    <input type="text" className="form-control form-control-sm" value={email.copia}
                      onChange={(e) => mudarEmail('copia', e.target.value)} />
                  </div>
                </div>
                <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
                  <div>
                    <label className="form-label small fw-semibold">Enviar como</label>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={email.operador_id}
                      onChange={(e) => mudarEmail('operador_id', e.target.value)}>
                      <option value="">— escolha —</option>
                      {(dados?.envio?.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.pronto ? '' : ' (sem senha de app)'}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={salvarEmails}
                    disabled={!!ocupado || !email.para.trim()} title="Guardar Para e Cópia para os próximos envios desta empresa">
                    <i className={`fas ${ocupado === 'salvar-email' ? 'fa-spinner fa-spin' : 'fa-save'} me-1`}></i>Salvar e-mails
                  </button>
                  {!email.previa && (
                    <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => verPrevia(email)} disabled={email.carregando}>
                      <i className={`fas ${email.carregando ? 'fa-spinner fa-spin' : 'fa-eye'} me-1`}></i>Atualizar prévia
                    </button>
                  )}
                </div>

                {email.carregando && !email.previa && <div className="small text-muted mt-3"><i className="fas fa-spinner fa-spin me-1"></i>Montando o e-mail…</div>}
                {email.previa && (<>
                  <div className="small mt-3 mb-2">
                    <div><b>Assunto:</b> {email.previa.assunto}</div>
                    <div><b>De:</b> {email.previa.de}</div>
                    <div><b>Para:</b> {email.previa.para || <span className="text-danger">informe o destinatário</span>}</div>
                    {email.previa.copia && <div><b>Cópia:</b> {email.previa.copia}</div>}
                    <div><b>Anexo:</b> <i className="fas fa-file-excel text-success me-1"></i>{email.previa.anexo}</div>
                  </div>
                  <div className="border rounded" style={{ maxHeight: 340, overflow: 'auto' }}>
                    <iframe title="Prévia do e-mail do adiantamento" srcDoc={email.previa.corpo_previa || email.previa.corpo_html}
                      sandbox="" style={{ width: '100%', height: 340, border: 0 }} />
                  </div>
                </>)}
              </div>
              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">A planilha Omie vai anexada — não precisa baixar nada.</span>
                <button className="btn btn-secondary" onClick={() => setEmail(null)} disabled={ocupado === 'email'}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
                <button className="btn btn-success" onClick={enviarEmail}
                  disabled={ocupado === 'email' || !email.previa || !email.para.trim() || !email.operador_id}
                  title={!email.previa ? 'Atualize a prévia antes de enviar' : undefined}>
                  <i className={`fas ${ocupado === 'email' ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                  {ocupado === 'email' ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div></div>
  );
}
